import chalk from "chalk"

import { CHAPERON_API_URL } from "../const/paths"
import { consoleLog } from "../utilities/helpers"

export async function getNewsData(location, countryCode) {
    consoleLog("getNewsData - location:", location, "countryCode:", countryCode)
    if (!location.timestamp) {
        // return []
        consoleLog(
            "No valid location timestamp provided when getting news data.",
        )
    }

    if (countryCode === "GLOBAL") {
        countryCode = "US"
    }

    countryCode = countryCode || "US"

    const url = `${CHAPERON_API_URL}/v1/news/get?minimum_risk=1&risk_type=crime&country=${countryCode}&language=en&category=general&limit=200`

    try {
        consoleLog(`Fetching news data from ${chalk.red(`Chaperon API`)}.`)
        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error("Error fetching news data:", error)
        throw error
    }
}

export async function getNewsSummary(location, countryCode) {
    if (!location.timestamp) {
        // return []
        consoleLog(
            "No valid location timestamp provided when getting news data.",
        )
    }

    if (countryCode === "GLOBAL") {
        countryCode = "US"
    }

    countryCode = countryCode || "US"

    const url = `${CHAPERON_API_URL}/v1/news/summary?minimum_risk=3&risk_type=crime&country=${countryCode}&language=en&category=general&limit=200`

    try {
        consoleLog(`Fetching news summary from ${chalk.red(`Chaperon API`)}.`)
        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error("Error fetching news data:", error)
        throw error
    }
}

export async function getEntityById(id) {
    if (!id) {
        consoleLog("No entities to fetch.")
        return []
    }

    let url = `${CHAPERON_API_URL}/v1/entities/get?id=${id}`

    try {
        consoleLog(`Fetching entity data from ${chalk.red(`Chaperon API`)}.`)
        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
        if (!response.ok) {
            // TODO: Handle error by displaying appropriate Toast
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error("Error fetching entity data:", error)
        // TODO: Handle error by displaying appropriate Toast
        throw error
    }
}

// A place's public summary: safety levels (never scores) and the overall
// overview, no login needed. Resolves to null for an unknown place.
export async function getPublicPlaceSummary(slug) {
    const response = await fetch(
        `${CHAPERON_API_URL}/v1/public/places/${encodeURIComponent(slug)}`
    )
    if (response.status === 404) return null
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
    }
    const body = await response.json()
    return body?.data ?? null
}

export async function getEntityBySlug(slug) {
    if (!slug) {
        consoleLog("No entities to fetch.")
        return []
    }

    let url = `${CHAPERON_API_URL}/v1/entities/get?slug=${slug}`

    try {
        consoleLog(`Fetching entity data from ${chalk.red(`Chaperon API`)}.`)
        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
        if (!response.ok) {
            // TODO: Handle error by displaying appropriate Toast
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error("Error fetching entity data:", error)
        // TODO: Handle error by displaying appropriate Toast
        throw error
    }
}

export async function getEntitiesData(names, country, address, limit = 1) {
    if (!names.length) {
        consoleLog("No entities to fetch.")
        return []
    }

    if (country === "USA") {
        country = "United States"
    }

    let url = `${CHAPERON_API_URL}/v1/entities/get?entities=${names}&address=${address}&limit=${limit}`

    if (country) {
        url += `&country=${country}`
    }

    try {
        consoleLog(`Fetching entities data from ${chalk.red(`Chaperon API`)}.`)
        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error("Error fetching entity data:", error)
        throw error
    }
}

export async function getEntitiesSpotlights(limit = 5) {
    let url = `${CHAPERON_API_URL}/v1/entities/get/spotlights?limit=${limit}`

    try {
        consoleLog(
            `Fetching spotlight entities data from ${chalk.red(
                `Chaperon API`,
            )}.`,
        )
        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error("Error fetching entity data:", error)
        throw error
    }
}

export async function getConcerns(
    country,
    advisoryId,
    category,
    type,
    limit = 1000,
) {
    let url = `${CHAPERON_API_URL}/v1/advisories/get/concerns?country=${country}&limit=${limit}`

    if (advisoryId) {
        url += `&advisoryId=${advisoryId}`
    }

    try {
        consoleLog(`Fetching concerns data from ${chalk.red(`Chaperon API`)}.`)
        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
        if (!response.ok) {
            // Return structure indicating component should be hidden
            return { original: [], structured: [], shouldHide: true }
        }

        const data = await response.json()
        // console.log("Concerns data:", JSON.stringify(data))

        // No concerns for this country: hide the card, as for a 404 before
        // the API returned an empty list instead.
        if (!Array.isArray(data) || data.length === 0) {
            return { original: [], structured: [], shouldHide: true }
        }

        // Process the data to group by category and then by type
        const concernsData = data.reduce((acc, concern) => {
            const { concern_category, concern_type } = concern

            // Find or create category
            let categoryGroup = acc.find(
                (cat) => cat.category === concern_category,
            )
            if (!categoryGroup) {
                categoryGroup = {
                    category: concern_category,
                    types: [],
                }
                acc.push(categoryGroup)
            }

            // Find or create type within category
            let typeGroup = categoryGroup.types.find(
                (type) => type.type === concern_type,
            )
            if (!typeGroup) {
                typeGroup = {
                    type: concern_type,
                    concerns: [],
                }
                categoryGroup.types.push(typeGroup)
            }

            // Add concern to type group
            typeGroup.concerns.push(concern)

            return acc
        }, [])

        return { original: data, structured: concernsData }
    } catch (error) {
        console.error("Error fetching concerns data:", error)
        // Return structure indicating component should be hidden
        return { original: [], structured: [], shouldHide: true }
    }
}

export async function submitNewReport(newReport) {
    let url = `${CHAPERON_API_URL}/v1/reports/new`

    try {
        consoleLog(`Submitting new report to ${chalk.red(`Chaperon API`)}.`)
        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(newReport),
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error("Error submitting new report:", error)
        throw error
    }
}

export async function getReports(limit = 20) {
    let url = `${CHAPERON_API_URL}/v1/reports/get?limit=${limit}`

    try {
        consoleLog(`Fetching reports data from ${chalk.red(`Chaperon API`)}.`)
        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error("Error fetching entity data:", error)
        throw error
    }
}
export async function getReviewsSummary(placeId) {
    if (!placeId) {
        consoleLog("getReviewsSummary: No Place ID provided to fetch.")
        return {}
    }

    let url = `${CHAPERON_API_URL}/v1/places/reviews/summary?placeId=${placeId}`

    try {
        consoleLog(
            `Fetching review summary data from ${chalk.red(`Chaperon API`)}.`,
        )
        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error("Error fetching review summary data:", error)
        throw error
    }
}

export async function createCommunity(communityData) {
    let url = `${CHAPERON_API_URL}/v1/community/create`

    try {
        consoleLog(`Creating community in ${chalk.red(`Chaperon API`)}.`)
        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(communityData),
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error("Error creating community:", error)
        throw error
    }
}

export async function getCommunities(userId) {
    if (!userId) {
        consoleLog("No user ID provided to fetch communities.")
        return { communities: [] }
    }

    let url = `${CHAPERON_API_URL}/v1/community/get?user_id=${userId}`

    try {
        consoleLog(`Fetching communities from ${chalk.red(`Chaperon API`)}.`)
        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        // Backend returns plain array, wrap it in object with communities property
        return { communities: Array.isArray(data) ? data : [] }
    } catch (error) {
        console.error("Error fetching communities:", error)
        throw error
    }
}

export async function updateCommunity(communityData) {
    if (!communityData.id) {
        consoleLog("No community ID provided to update.")
        throw new Error("Community ID is required")
    }

    let url = `${CHAPERON_API_URL}/v1/community/update`

    try {
        consoleLog(`Updating community in ${chalk.red(`Chaperon API`)}.`)
        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(url, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(communityData),
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error("Error updating community:", error)
        throw error
    }
}

export async function patchCommunity(communityId, userId, updates) {
    if (!communityId || !userId) {
        consoleLog("communityId and userId are required for patchCommunity.")
        throw new Error("communityId and userId are required fields")
    }
    if (!updates || (!updates.name && !updates.chiefUpdates)) {
        throw new Error("At least one of name or chiefUpdates must be provided")
    }

    const url = `${CHAPERON_API_URL}/v1/community/${communityId}`

    try {
        consoleLog(
            `Patching community ${communityId} in ${chalk.red(`Chaperon API`)}.`,
        )
        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(url, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ user_id: userId, ...updates }),
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }
        return await response.json()
    } catch (error) {
        console.error("Error patching community:", error)
        throw error
    }
}

export async function addChaperee(chapereeData) {
    if (
        !chapereeData.member_id ||
        !chapereeData.type ||
        !chapereeData.user_id ||
        !chapereeData.community_id
    ) {
        consoleLog(
            "member_id, type, user_id, and community_id are required for creating a chaperee",
        )
        throw new Error(
            "member_id, type, user_id, and community_id are required fields",
        )
    }

    let url = `${CHAPERON_API_URL}/v1/community/chaperee/add`

    try {
        consoleLog(`Creating chaperee in ${chalk.red(`Chaperon API`)}.`)
        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(chapereeData),
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error("Error creating chaperee:", error)
        throw error
    }
}

export async function updateChapereeOrder(orderData) {
    if (
        !orderData.community_id ||
        !orderData.user_id ||
        !orderData.order_updates ||
        !Array.isArray(orderData.order_updates)
    ) {
        consoleLog(
            "community_id, user_id, and order_updates array are required",
        )
        throw new Error(
            "community_id, user_id, and order_updates array are required fields",
        )
    }

    let url = `${CHAPERON_API_URL}/v1/community/chaperee/order`

    try {
        consoleLog(`Updating chaperee order in ${chalk.red(`Chaperon API`)}.`)
        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(url, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(orderData),
        })
        if (!response.ok) {
            let errorMsg = `HTTP error! status: ${response.status}`
            try {
                const errorData = await response.json()
                if (errorData && errorData.message) {
                    errorMsg = errorData.message
                }
            } catch (e) {
                // Ignore json parsing error if no body
            }
            throw new Error(errorMsg)
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error("Error updating chaperee order:", error)
        throw error
    }
}

export async function getEnrichedChaperee(chapereeId) {
    if (!chapereeId) {
        consoleLog("No chaperee ID provided.")
        throw new Error("Chaperee ID is required")
    }

    let url = `${CHAPERON_API_URL}/v1/community/chaperee/get?chaperee_id=${chapereeId}`

    try {
        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error("Error fetching enriched chaperee:", error)
        throw error
    }
}

export async function deleteCommunity(communityData) {
    if (!communityData.id || !communityData.user_id) {
        consoleLog("Community ID and user ID are required to delete.")
        throw new Error("Community ID and user ID are required")
    }

    let url = `${CHAPERON_API_URL}/v1/community/delete`

    try {
        consoleLog(`Deleting community from ${chalk.red(`Chaperon API`)}.`)
        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(url, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                id: communityData.id,
                user_id: communityData.user_id,
            }),
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error("Error deleting community:", error)
        throw error
    }
}

export async function deleteChaperees(deleteData) {
    if (
        !deleteData.community_id ||
        !deleteData.user_id ||
        !deleteData.chaperee_ids ||
        !Array.isArray(deleteData.chaperee_ids)
    ) {
        consoleLog("community_id, user_id, and chaperee_ids array are required")
        throw new Error(
            "community_id, user_id, and chaperee_ids array are required fields",
        )
    }

    let url = `${CHAPERON_API_URL}/v1/community/chaperee/delete`

    try {
        consoleLog(`Deleting chaperee(s) from ${chalk.red(`Chaperon API`)}.`)

        // Format deletions array as per API spec: array of {community_id, chaperee_id} objects
        const deletions = deleteData.chaperee_ids.map((chaperee_id) => ({
            community_id: deleteData.community_id,
            chaperee_id: chaperee_id,
        }))

        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(url, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                user_id: deleteData.user_id,
                deletions: deletions,
            }),
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error("Error deleting chaperee(s):", error)
        throw error
    }
}

export async function updateChaperee(updateData) {
    if (
        !updateData.chaperee_id ||
        !updateData.user_id ||
        !updateData.community_id
    ) {
        consoleLog("chaperee_id, user_id, and community_id are required")
        throw new Error(
            "chaperee_id, user_id, and community_id are required fields",
        )
    }

    let url = `${CHAPERON_API_URL}/v1/community/chaperee/update`

    try {
        consoleLog(`Updating chaperee from ${chalk.red(`Chaperon API`)}.`)
        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(url, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(updateData),
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error("Error updating chaperee:", error)
        throw error
    }
}

export async function patchChaperee(communityId, chapereeId, userId, updates) {
    if (!communityId || !chapereeId || !userId) {
        consoleLog(
            "communityId, chapereeId, and userId are required for patchChaperee.",
        )
        throw new Error(
            "communityId, chapereeId, and userId are required fields",
        )
    }
    if (
        !updates ||
        typeof updates !== "object" ||
        Object.keys(updates).length === 0
    ) {
        throw new Error("updates object with at least one property is required")
    }

    const url = `${CHAPERON_API_URL}/v1/community/${communityId}/chaperee/${chapereeId}`

    try {
        consoleLog(
            `Patching chaperee ${chapereeId} in community ${communityId} from ${chalk.red(`Chaperon API`)}.`,
        )
        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(url, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ user_id: userId, updates }),
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }
        return await response.json()
    } catch (error) {
        console.error("Error patching chaperee:", error)
        throw error
    }
}

export async function insertChaperee(insertData) {
    if (
        !insertData.user_id ||
        !insertData.community_id ||
        !insertData.chaperee_id
    ) {
        consoleLog("user_id, community_id, and chaperee_id are required")
        throw new Error(
            "user_id, community_id, and chaperee_id are required fields",
        )
    }

    let url = `${CHAPERON_API_URL}/v1/community/chaperee/insert`

    try {
        consoleLog(
            `Inserting chaperee into community from ${chalk.red(
                `Chaperon API`,
            )}.`,
        )
        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(insertData),
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error("Error inserting chaperee:", error)
        throw error
    }
}

export async function getNotifications(userId, options = {}) {
    if (!userId) {
        return { success: false, message: "User ID is required" }
    }

    const token = localStorage.getItem("chaperon_auth_token")
    if (!token) {
        return { success: false, message: "No authentication token found" }
    }

    const url = new URL(`${CHAPERON_API_URL}/v1/notification/get`)
    url.searchParams.append("user_id", userId)
    if (options.limit) url.searchParams.append("limit", options.limit)
    if (options.type) url.searchParams.append("type", options.type)
    if (options.unread_only)
        url.searchParams.append("unread_only", options.unread_only)

    try {
        consoleLog(`Fetching notifications from ${chalk.red(`Chaperon API`)}.`)
        const response = await fetch(url.toString(), {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        consoleLog("Error fetching notifications:", error)
        return { success: false, message: "Error fetching notifications" }
    }
}

export async function clearNotification(userId, notificationId) {
    if (!userId || !notificationId) {
        return {
            success: false,
            message: "User ID and notification ID are required",
        }
    }

    const token = localStorage.getItem("chaperon_auth_token")
    if (!token) {
        return { success: false, message: "No authentication token found" }
    }

    const url = new URL(`${CHAPERON_API_URL}/v1/notification/clear`)
    url.searchParams.append("user_id", userId)
    url.searchParams.append("notification_id", notificationId)

    try {
        consoleLog(`Clearing notification from ${chalk.red(`Chaperon API`)}.`)
        const response = await fetch(url.toString(), {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        consoleLog("Error clearing notification:", error)
        return { success: false, message: "Error clearing notification" }
    }
}

export async function clearAllNotifications(userId) {
    if (!userId) {
        return { success: false, message: "User ID is required" }
    }

    const token = localStorage.getItem("chaperon_auth_token")
    if (!token) {
        return { success: false, message: "No authentication token found" }
    }

    const url = new URL(`${CHAPERON_API_URL}/v1/notification/clear/all`)
    url.searchParams.append("user_id", userId)

    try {
        consoleLog(
            `Clearing all notifications from ${chalk.red(`Chaperon API`)}.`,
        )
        const response = await fetch(url.toString(), {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        consoleLog("Error clearing all notifications:", error)
        return { success: false, message: "Error clearing all notifications" }
    }
}

export async function readAllNotifications(userId) {
    if (!userId) {
        return { success: false, message: "User ID is required" }
    }

    const token = localStorage.getItem("chaperon_auth_token")
    if (!token) {
        return { success: false, message: "No authentication token found" }
    }

    const url = new URL(`${CHAPERON_API_URL}/v1/notification/read/all`)
    url.searchParams.append("user_id", userId)

    try {
        consoleLog(
            `Marking all notifications as read from ${chalk.red(`Chaperon API`)}.`,
        )
        const response = await fetch(url.toString(), {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        consoleLog("Error reading all notifications:", error)
        return {
            success: false,
            message: "Error marking notifications as read",
        }
    }
}

export async function unreadAllNotifications(userId) {
    if (!userId) {
        return { success: false, message: "User ID is required" }
    }

    const token = localStorage.getItem("chaperon_auth_token")
    if (!token) {
        return { success: false, message: "No authentication token found" }
    }

    const url = new URL(`${CHAPERON_API_URL}/v1/notification/unread/all`)
    url.searchParams.append("user_id", userId)

    try {
        consoleLog(
            `Marking all notifications as unread from ${chalk.red(`Chaperon API`)}.`,
        )
        const response = await fetch(url.toString(), {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        consoleLog("Error unreading all notifications:", error)
        return {
            success: false,
            message: "Error marking notifications as unread",
        }
    }
}

export async function readNotification(userId, notificationId) {
    if (!userId || !notificationId) {
        return {
            success: false,
            message: "User ID and notification ID are required",
        }
    }

    const token = localStorage.getItem("chaperon_auth_token")
    if (!token) {
        return { success: false, message: "No authentication token found" }
    }

    const url = new URL(`${CHAPERON_API_URL}/v1/notification/read`)
    url.searchParams.append("user_id", userId)
    url.searchParams.append("notification_id", notificationId)

    try {
        consoleLog(
            `Marking notification as read from ${chalk.red(`Chaperon API`)}.`,
        )
        const response = await fetch(url.toString(), {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        consoleLog("Error marking notification as read:", error)
        return { success: false, message: "Error marking notification as read" }
    }
}

export async function unreadNotification(userId, notificationId) {
    if (!userId || !notificationId) {
        return {
            success: false,
            message: "User ID and notification ID are required",
        }
    }

    const token = localStorage.getItem("chaperon_auth_token")
    if (!token) {
        return { success: false, message: "No authentication token found" }
    }

    const url = new URL(`${CHAPERON_API_URL}/v1/notification/unread`)
    url.searchParams.append("user_id", userId)
    url.searchParams.append("notification_id", notificationId)

    try {
        consoleLog(
            `Marking notification as unread from ${chalk.red(`Chaperon API`)}.`,
        )
        const response = await fetch(url.toString(), {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        consoleLog("Error marking notification as unread:", error)
        return {
            success: false,
            message: "Error marking notification as unread",
        }
    }
}
